import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {CartDecoratorErrorHandlingService} from '@app/checkout/services/cart-decorator-error-handling.service';
import {ProductPageDecoratorService} from '@app/product/services/product-page-decorator.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {DcpCustomerRegistrationDecoratorService} from '@app/services/dcp-customer-registration-decorator.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {UserRegistrationConfigService} from '@app/services/user-registration/user-registration-config.service';
import {UserRegistrationRedirectService} from '@app/services/user-registration/user-registration-redirect.service';
import {DcpUserService} from '@app/services/user/dcp-user.service';
import {TransactionType} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {Product, User} from '@spartacus/core';
import {combineLatest, Observable, of, throwError} from 'rxjs';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';

export interface CartErrorValidation {
  code: string | null;
  description: string | null;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ValidateCartEntryService {

  // Gets set after subscriber.next('subscribe to product') was called
  private variantProduct: Product;

  constructor(
    private transactionTypeService: TransactionTypeService,
    private productPageDecoratorService: ProductPageDecoratorService,
    private authenticationService: DcpAuthService,
    private userService: DcpUserService,
    private router: Router,
    private userRegistrationRedirectService: UserRegistrationRedirectService,
    private userRegistrationConfigService: UserRegistrationConfigService,
    private cartDecoratorErrorHandlingService: CartDecoratorErrorHandlingService,
    private dcpConfigService: DcpConfigService,
    private dcpCustomerRegistrationDecoratorService: DcpCustomerRegistrationDecoratorService
  ) {
  }

  public validateCta(variantProduct$: Observable<Product>, orderEntry?: OrderEntry): Observable<any> {
    let transactionTypeRef;
    return combineLatest([
      this.authenticationService.isUserLoggedIn(),
      this.transactionTypeService.getTransactionTypeByUrlOrOrderEntry(orderEntry)
    ]).pipe(untilDestroyed(this),
      map(([isUserLoggedIn, transactionType]) => {
        if (!isUserLoggedIn && transactionType?.isLoginRequired) {
          this.authenticationService.login(this.router.url);
          return undefined;
        } else {
          transactionTypeRef = transactionType;
          return transactionType;
        }
      }),
      filter(transactionType => !!transactionType),
      switchMap(() => this.dcpCustomerRegistrationDecoratorService.getCurrentUser()),
      switchMap((user) => this.verifyRequirementsForTransaction(user, transactionTypeRef)),
      switchMap(() => variantProduct$),
      tap(variantProduct => this.variantProduct = variantProduct),
      switchMap((variantProduct) => this.dcpConfigService.getCommercialModelConfig(variantProduct, transactionTypeRef?.code)),
      switchMap((commercialModelConfig) => {
        if (commercialModelConfig.isValidationRequired) {
          return this.productPageDecoratorService.getCtaValidation(this.variantProduct, transactionTypeRef)
            .pipe(
              catchError(httpErrorResponse => {
                this.handleCtaError(httpErrorResponse);
                return throwError(httpErrorResponse);
              })
            );
        } else {
          return of('subscribe to product');
        }
      }));
  }

  /**
   * Registration guard for 'legacy' configurator.
   * Redirects to /selectionType page unknown customers
   *
   * @param user
   * @param transactionType
   * @private
   */
  private verifyRequirementsForTransaction(user: User, transactionType: TransactionType): Observable<boolean> {
    return this.userService.isCustomerKnown(user).pipe(untilDestroyed(this),
      tap(isCustomerKnown => {
        if (!isCustomerKnown && transactionType?.isFullRegistrationRequired) {
          this.userRegistrationConfigService.fetchConfigFromUrl();
          this.userRegistrationRedirectService.registerCurrentUser();
        }
      })
    );
  }

  private handleCartError(e: { error?: any; code?: string; status?: string }): CartErrorValidation {
    const validation = {
      code: e?.code ? e.code : e?.status,
      description: e?.error?.errors?.length ? e.error.errors.map(r => r?.message)?.join() : null
    };
    this.cartDecoratorErrorHandlingService.handleErrorByValidationCode(validation, null).then();
    return validation;
  }

  private handleCtaError(e: HttpErrorResponse): void {
    try {
      let cta;
      e.error.entitlements.forEach((entitlement) => {
        entitlement.callToActions.forEach((callToAction) => {
          if (callToAction.error) {
            cta = callToAction;
          }
        });
      });
      this.cartDecoratorErrorHandlingService.handleErrorByCtaValidation(cta);
    } catch (err) {
      this.handleCartError(e?.error?.target?.response);
    }
  }
}
